/**
 * Modules Store - Zustand store for managing module access state
 */

import { create } from 'zustand';
import { modulesApi } from '../utils/api';

interface ModuleAccess {
    hasAccess: boolean;
    moduleType: string;
    source?: 'bundle' | 'addon' | 'trial' | 'promo' | 'enterprise' | 'free';
    expiresAt?: string;
    isTrialing?: boolean;
    trialEndsAt?: string;
}

interface ModuleCatalogItem {
    id: string;
    type: string;
    name: string;
    description: string;
    category: string;
    priceMonthly: number;
    priceYearly: number;
    features: string[];
    icon: string;
    color: string;
    isBeta: boolean;
}

interface Bundle {
    id: string;
    slug: string;
    name: string;
    description: string;
    priceMonthly: number;
    priceYearly: number;
    discountPercent: number;
    targetPersona: string;
    icon: string;
    color: string;
    isPopular: boolean;
    modules: { moduleType: string; isCore: boolean; module: ModuleCatalogItem }[];
}

interface ModulesState {
    // Module access
    modules: ModuleAccess[];
    activeBundle: string | null;
    canUpgrade: boolean;

    // Catalog
    catalog: ModuleCatalogItem[];
    catalogByCategory: Record<string, ModuleCatalogItem[]>;
    bundles: Bundle[];
    freeModules: string[];

    // Loading state
    isLoading: boolean;
    error: string | null;

    // Actions
    fetchMyModules: () => Promise<void>;
    fetchCatalog: () => Promise<void>;
    checkModuleAccess: (moduleType: string) => boolean;
    startTrial: (moduleType: string) => Promise<{ success: boolean; message?: string }>;
    hasModule: (moduleType: string) => ModuleAccess | undefined;
    clearError: () => void;
}

export const useModulesStore = create<ModulesState>((set, get) => ({
    // Initial state
    modules: [],
    activeBundle: null,
    canUpgrade: true,
    catalog: [],
    catalogByCategory: {},
    bundles: [],
    freeModules: ['analytics'],
    isLoading: false,
    error: null,

    // Fetch user's modules
    fetchMyModules: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await modulesApi.getMyModules();
            const data = response.data.data;

            set({
                modules: data.modules,
                activeBundle: data.activeBundle,
                canUpgrade: data.canUpgrade,
                isLoading: false
            });
        } catch (error: any) {
            set({
                error: error.response?.data?.error || 'Failed to fetch modules',
                isLoading: false
            });
        }
    },

    // Fetch catalog
    fetchCatalog: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await modulesApi.getCatalog();
            const data = response.data.data;

            set({
                catalog: data.modules,
                catalogByCategory: data.modulesByCategory,
                bundles: data.bundles,
                freeModules: data.freeModules,
                isLoading: false
            });
        } catch (error: any) {
            set({
                error: error.response?.data?.error || 'Failed to fetch catalog',
                isLoading: false
            });
        }
    },

    // Check if user has access to a module
    checkModuleAccess: (moduleType: string) => {
        const { modules, freeModules } = get();

        // Free modules are always accessible
        if (freeModules.includes(moduleType)) {
            return true;
        }

        const moduleAccess = modules.find(m => m.moduleType === moduleType);
        return moduleAccess?.hasAccess || false;
    },

    // Get module access info
    hasModule: (moduleType: string) => {
        const { modules, freeModules } = get();

        if (freeModules.includes(moduleType)) {
            return {
                hasAccess: true,
                moduleType,
                source: 'free' as const
            };
        }

        return modules.find(m => m.moduleType === moduleType);
    },

    // Start trial
    startTrial: async (moduleType: string) => {
        set({ isLoading: true, error: null });
        try {
            const response = await modulesApi.startTrial(moduleType);

            // Refresh modules
            await get().fetchMyModules();

            return { success: true, message: response.data.message };
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to start trial';
            set({
                error: message,
                isLoading: false
            });
            return { success: false, message };
        }
    },

    clearError: () => set({ error: null })
}));

// Helper hook for module access check in components
export function useModuleAccess(moduleType: string) {
    const { checkModuleAccess, hasModule, isLoading } = useModulesStore();

    return {
        hasAccess: checkModuleAccess(moduleType),
        moduleInfo: hasModule(moduleType),
        isLoading
    };
}
